import React, { useEffect, useState } from 'react';

import { Formik } from 'formik';
import * as yup from "yup";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FolderIcon } from "@heroicons/react/outline";

import { useToastAction } from '../../../hooks/useToastAction';
import Modal from '../../../components/Modal';

import { Button as CmpButton } from "../../../components/Button";
import { Label as CmpLabel } from "../../../components/Label";
import { Panel as CmpPanel } from "../../../components/Panel";

function FolderItem({ item, index, indent, onSelect }) {
  const handleClick = () => {
    onSelect(item, index);
  };

  // A root is a non-static folder with no parent — only roots can hold subfolders.
  const isRoot = !item.parentFolderId;

  return (
    <div
      tabIndex={0}
      style={indent ? { paddingLeft: '1.75rem' } : undefined}
      className={`flex my-1 px-3 py-1 text-sm group font-medium cursor-pointer items-center rounded-md ${
        item.isSelected
          ? 'bg-brand-blue text-gray-100'
          : 'text-gray-700 hover:bg-brand-blue-hover hover:text-gray-200 dark:text-gray-400'
      }`}
      onClick={handleClick}
    >
      <item.icon
        className={`mr-1.5 ${item.isSelected ? 'text-gray-100 h-6 w-6' : 'text-gray-700 group-hover:text-gray-200 h-6 w-6 dark:text-gray-400'}`}
        aria-hidden="true"
      />
      <span className={`truncate whitespace-nowrap overflow-hidden overflow-ellipsis select-none`}>
        {item.name}
      </span>
    </div>
  );
}

export function EditSelectedTextLibraryFoldersModal({ modalHelper, title, onConfirm, isSaving, workflowTriggers }) {
  const [isBusy, setIsBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState("");

  const [folderTree, setFolderTree] = useState([]);

  const loadAction = useToastAction();
  
  useEffect(async () => {
    if (modalHelper.activeItem) {
      setBusyMessage("Loading textlibrary folders...");
      setIsBusy(true);

      const selectedItems = modalHelper.activeItem.item?.split(";") || [];
      await loadAction.executeAsync(async () => {
        try {
          const ft = [];

          buildFolderTree(modalHelper.activeItem.loadedTextLibraryFolders).forEach((root) => {
            const rootItem = {
              ...root,
              icon: FolderIcon,
              isSelected: selectedItems.includes(root.id),
              children: (root.children || []).map((c) => ({ ...c, icon: FolderIcon, isSelected: selectedItems.includes(c.id) })),
            };
            if (!rootItem.isPrivate)
              ft.push(rootItem);
          });
          setFolderTree(ft);
        } finally {
          setIsBusy(false);
          setBusyMessage(null);
        }
      }, "Failed to load (EditSelectedTextLibraryFoldersModal)")
    }
  }, [modalHelper.activeItem])

  const handleSubmit = async (values) => {
    await onConfirm(modalHelper.activeItem?.props, getSelectedFolders());
  }

  const getSelectedFolders = () => {
    let selectedFolders = "";
    folderTree.flatMap(a => [a].concat(a.children)).forEach(a => {
        if (a.isSelected)
        {
            if (selectedFolders.length > 0)
                selectedFolders += ";";
            selectedFolders += a.id;
        }
    });
    return selectedFolders;
  }

  const buildFolderTree = (folders) => {
    if (!Array.isArray(folders))
      return [];
    const byId = new Map(folders.map((f) => [f.id, f]));
    const roots = [];
    const childrenByParent = new Map();
    folders.forEach((f) => {
      const parentId = f.parentFolderId;
      if (parentId && byId.has(parentId)) {
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
          childrenByParent.get(parentId).push(f);
        } else {
          roots.push(f);
        }
    });
    return roots.map((root) => ({ ...root, children: childrenByParent.get(root.id) || [] }));
  };
  
  const renderItem = (item, index, indent, onSelect) => (
    <FolderItem 
      key={item.id}
      item={item}
      index={index}
      indent={indent}
      onSelect={onSelect}
    />
  );

  const onToggleSelectFolder = (item) => {
    const newFolderTree = [...folderTree];
    const newItem = newFolderTree.flatMap(a => [a].concat(a.children)).find(
      a => a.id === item.id
    );
    if (!!newItem)
    {
        newItem.isSelected = !item.isSelected;
        setFolderTree(newFolderTree);
    }
  };

  const renderRoot = (root, index) => (
    <div key={root.id}>
      {renderItem(root, index, false, () => onToggleSelectFolder(root))}
      {(root.children || []).map((child) => renderItem(child, index, true, () => onToggleSelectFolder(child)))}
    </div>
  );

  return (
    <Modal
      isOpen={modalHelper.isOpen}
      onClose={() => { modalHelper.close() }}
      title={title}
    >
        {isBusy && (
        <>
          <div className="flex justify-center">
            <FontAwesomeIcon icon={faSpinner} className="text-white" size="4x" spin bounce />
          </div>
          <div className="flex justify-center mt-4">
            <CmpLabel text={busyMessage} />
          </div>
        </>
      )}

      {!isBusy && (
        <>
          <div>
            <Formik
              // enableReinitialize={true}
              initialValues={{
                textLibraryFoldersIds: modalHelper.activeItem?.item.textLibraryFolders,
              }}
              validationSchema={yup.object().shape({
                textLibraryFoldersIds: yup.string(),
              }).test(
                'processesTest',
                null,
                (obj) => {
                  return true;
                }
              )}
              onSubmit={handleSubmit}
            >
              {({ values, errors, ...props }) => (
                <>
                <div className='mt-4'>
                    {/* <CmpLabel text="Folders:" additionalClass='mb-2'/> */}
                    <CmpPanel>
                    {folderTree.map((root, index) => renderRoot(root, index + 1))}
                    </CmpPanel>
                </div>

                <div className='grid grid-cols-2 mt-6'>
                    <div className='flex gap-x-4'>
                    </div>
                    <div className='flex gap-x-4 justify-end'>
                    <CmpButton
                        variant={CmpButton.variants.secondary}
                        className="justify-center"
                        disabled={false}
                        onClick={() => {
                            modalHelper.close();
                        }}
                        >
                        Cancel
                    </CmpButton>
                    <CmpButton
                        variant={CmpButton.variants.primary}
                        className="justify-center"
                        disabled={false}
                        // type={"submit"}
                        onClick={() => {
                            handleSubmit(values);
                        }}
                        >
                        Save
                    </CmpButton>
                    </div>
                </div>
                </>
              )}
            </Formik>
          </div>
        </>
      )}
    </Modal>
  )
}
